<template>
  <div class="mobile-header-container">
    <v-row class="mobile-header" no-gutters align="center">
      <!-- Left Section: Logo and Title -->
      <v-col cols="8" class="mobile-header-left">
        <div class="mobile-header-content">
          <!-- Logo Icon -->
          <div class="mobile-logo">
             <img src="@/assets/images/BB-logo-1.png" height="28" alt="Blubirch Logo" @click="navigateHome"/>
          </div>
          <!-- Title -->
          <div class="mobile-title">
            <span class="mobile-title-text">{{ displayHeaderTitle }}</span>
          </div>
        </div>
      </v-col>

      <!-- Right Section: Hamburger Menu -->
      <v-col cols="4" class="mobile-header-right">
        <div class="mobile-header-actions">
          <!-- Filter Icon -->
          <v-btn icon class="mobile-menu-btn mr-1" @click="filterDialog = true">
            <v-icon color="white" size="24">mdi-filter-outline</v-icon>
          </v-btn>

          <!-- Location Filter Bottom Sheet -->
          <v-bottom-sheet v-model="filterDialog" inset content-class="bottom-sheet-custom">
            <v-card class="rounded-t-xl pb-0" style="border-radius: 24px 24px 0 0 !important;">
              <div style="width: 40px; height: 4px; background-color: #e0e0e0; border-radius: 2px; margin: 12px auto;"></div>
              
              <v-card-title class="font-weight-bold pt-2 pb-5" style="font-size: 18px; color: #333;">
                Select Location
              </v-card-title>
              
              <v-card-text class="pb-10">
                <v-autocomplete
                  v-model="current_warehouse"
                  :items="filteredMenuItems"
                  item-title="code"
                  item-value="id"
                  multiple
                  variant="outlined"
                  dense
                  :label="menu_items && menu_items[0] ? (menu_items[0].code.length > 15 ? menu_items[0].code.slice(0, 15) + '...' : menu_items[0].code) : 'Select'"
                  :item-props="getItemProps"
                  hide-details
                  class="location-autocomplete text-left"
                  prepend-inner-icon="mdi-magnify"
                >
                  <template #selection="{ item, index }">
                    <span class="mt-4">
                      <span v-if="index < 1">
                        {{ truncateText(item.title) }}<span v-if="index < current_warehouse.length - 1">, </span>
                      </span>
                      <span v-if="index === 1">... ({{ current_warehouse.length }})</span>
                    </span>
                  </template>
                </v-autocomplete>
              </v-card-text>
              
              <v-card-actions class="pa-0">
                <v-btn 
                  color="#0052cc" 
                  class="text-none font-weight-bold m-0"
                  variant="flat"
                  height="48" 
                  style="border-radius: 0; width: 100%; margin: 0; letter-spacing: 0.5px; font-size: 16px; color: white !important; background-color: #0357d0 !important" 
                  :disabled="!current_warehouse || !current_warehouse.length" 
                  @click="changeWarehouse"
                >
                  Done
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-bottom-sheet>

          <!-- Hamburger Menu -->
          <v-dialog 
            v-model="drawerMenu" 
            fullscreen 
            transition="fade-transition"
            style="z-index: 9999;"
          >
            <template #activator="{ props }">
              <v-btn icon class="mobile-menu-btn" v-bind="props">
                <v-icon color="white" size="26">mdi-menu</v-icon>
              </v-btn>
            </template>

            <v-card style="background-color: #00114B; border-radius: 0; min-height: 100vh;" class="ma-0">
              <v-card-text class="pa-4 pt-6">
                <!-- Top Rounded Bar -->
                <v-row class="align-center rounded-lg mx-0 mb-6 px-2" style="background-color: #e0eaf5; min-height: 48px;">
                  <v-icon size="40" @click="drawerMenu = false">mdi-chevron-left</v-icon>
                  <v-spacer></v-spacer>
                  <img src="@/assets/images/BB-logo-1.png" height="42" class="mr-2" style="object-fit: contain;" />
                  <span class="font-weight-bold" style="color: #1a237e; font-size: 18px;">Blubirch</span>
                  <v-spacer></v-spacer>
                  <div style="width: 36px;"></div>
                </v-row>

                <!-- Tag ID / IRD Toggle -->
                <div class="mb-4">
                  <v-btn-toggle v-model="toggle_button" class="rounded-lg w-100" style="max-height: 48px; background-color: #ffffff;" @update:modelValue="toggleButtonValue" mandatory>
                    <v-btn value="tag" class="text-none" size="large" style="flex: 1;" :style="toggle_button === 'tag' ? 'background-color: #e3f2fd; color: #00114B;' : 'background-color: #ffffff; color: #00114B;'">
                      Tag ID
                    </v-btn>
                    <v-btn value="ird" class="text-none" size="large" style="flex: 1;" :style="toggle_button === 'ird' ? 'background-color: #e3f2fd; color: #00114B;' : 'background-color: #ffffff; color: #00114B;'">
                      IRD
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Search -->
                <div>
                  <v-text-field 
                    v-model="search" 
                    prepend-inner-icon="mdi-magnify" 
                    class="rounded-lg mb-6 search-input-override" 
                    placeholder="Search Item"
                    append-inner-icon="mdi-fullscreen" 
                    color="primary" 
                    variant="solo"
                    density="comfortable"
                    bg-color="white"
                    hide-details
                    @keydown.enter="searchItem"
                  ></v-text-field>
                </div>

                <v-divider color="white" class="mb-4 border-opacity-25"></v-divider>

                <!-- User Info -->
                <div class="d-flex align-center mb-4 px-2">
                  <v-icon color="white" size="36" class="mr-3">mdi-account-circle</v-icon>
                  <div>
                    <div class="text-white font-weight-bold text-subtitle-1 lh-1" style="line-height: 1;">{{ user.username || user.first_name || 'User' }}</div>
                    <div class="text-white-50 text-caption mt-1">{{ user.email }}</div>
                  </div>
                </div>

                <!-- Navigation Items -->
                <v-list density="compact" bg-color="transparent" theme="dark" class="px-0">
                  <!-- <v-list-item 
                    v-for="(item, index) in navItems" 
                    :key="index"
                    :to="item.link"
                    class="rounded-lg mb-1"
                    @click="drawerMenu = false"
                  >
                    <template #prepend>
                      <v-icon size="24" color="white" class="mr-3">{{ item.icon }}</v-icon>
                    </template>
                    <v-list-item-title class="text-white font-weight-medium">
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item> -->

                  <!-- <v-divider class="my-3 border-opacity-25" color="white"></v-divider> -->

                  <!-- Logout -->
                  <v-list-item @click="signOut" :disabled="logoutInProgress" class="rounded-lg">
                    <template #prepend>
                      <v-icon size="24" color="#f44336" class="mr-3">mdi-logout-variant</v-icon>
                    </template>
                    <v-list-item-title class="font-weight-medium text-error" style="color: #f44336;">
                      Logout
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'MobileHeaderComponent',
  props: {
    headerTitle: {
      type: String,
      default: ''
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    backRoute: {
      type: Object,
      default: () => ({ name: 'rfDisposition' })
    }
  },
  data() {
    return {
      drawerMenu: false,
      filterDialog: false,
      menu_items: [],
      current_warehouse: null,
      logoutInProgress: false,
      user: {},
      organization_name: '',
      appName: 'RIMS',
      toggle_button: 'tag',
      search: ''
    };
  },
  computed: {
    filteredMenuItems() {
      if (!this.current_warehouse || !this.menu_items) return [];
      const isAllRPASelected = this.current_warehouse.includes(0);
      if (isAllRPASelected) {
        return this.menu_items.map(item => ({
          ...item,
          code : `${item.code} (${item.name})`,
          disabled: item.id !== 0
        }));
      }
      return this.menu_items.map(item => ({
        ...item,
         code : `${item.code} (${item.name})`,
        disabled: false
      }));
    },
    navItems() {
      const organization = this.$store.state.organization_name;
      const roles = this.$store.state.currentUser?.roles || [];
      const items = [];

      // Disposition
      items.push({
        text: 'Disposition',
        icon: 'mdi-swap-horizontal',
        link: { name: organization === 'Voltas' || organization === 'Reliance' ? 'reverseDisposition' : 'rfDisposition' },
        color: '#1a237e'
      });

      // Inward
      items.push({
        text: 'Inward',
        icon: 'mdi-inbox-arrow-down',
        link: { name: 'inward' },
        color: '#1a237e'
      });

      // Grading
      items.push({
        text: 'Grading',
        icon: 'mdi-star-check',
        link: { name: 'grading' },
        color: '#1a237e'
      });

      // Put Away / Pick Up
      items.push({
        text: 'Put Away / Pick Up',
        icon: 'mdi-package-up',
        link: { name: 'PutAway' },
        color: '#1a237e'
      });

      // Dispatch
      items.push({
        text: 'Dispatch',
        icon: 'mdi-truck-fast',
        link: { name: 'NewDispatch' },
        color: '#1a237e'
      });

      return items;
    },
    displayHeaderTitle() {
      if (this.$route.name === 'ItemInfo') {
        return this.$store.state.searched_ird === 'ird' ? 'IRD' : 'Item Info';
      } else {
        return this.appName;
      }
    }
  },
  watch: {
    '$store.state.searched_tagid': function(newTab) {
      this.search = newTab;
    },
    current_warehouse(newVal) {
      if (!newVal) return;
      const allRPASelected = newVal.includes(0);
      if (allRPASelected && newVal.length > 1) {
        this.$nextTick(() => {
          this.current_warehouse = [0];
        });
      }
    }
  },
  created() {
    this.user = this.$store.state.currentUser;
    this.organization_name = this.$store.state.organization_name;
    this.appName = 'RIMS';
    
    // RPA Initialization
    this.menu_items = this.$store.state.currentUser?.available_distribution_centers || [];
    this.current_warehouse = [...(this.$store.state.current_warehouse || [])];    
    if (this.menu_items.length === 1) {
      const singleItem = this.menu_items[0].id;
      this.current_warehouse = this.menu_items;
      this.$store.commit('setCurrentWarehouse', { distribution_id: [singleItem] });
    }

    window.addEventListener('close-mobile-drawer', this.handleCloseDrawer);
    },
    beforeUnmount() {
      window.removeEventListener('close-mobile-drawer', this.handleCloseDrawer);
    },
  methods: {
    handleCloseDrawer() {
      this.drawerMenu = false;
    },
    getItemProps(item) {
        return { disabled: item.disabled };
    },
    truncateText(text) {
      if (!text) return '';
      return text.length > 6 ? text.slice(0, 8) + '...' : text;
    },
    changeWarehouse(){
      this.filterDialog = false;
      if (this.current_warehouse == 0){
        this.$store.commit("setCurrentWarehouse", {distribution_id: ''})
        this.current_warehouse = null
      }else{
        this.$store.commit("setCurrentWarehouse", {distribution_id: this.current_warehouse})
      }
      this.$router.go()
    },
    goBack() {
      if (this.backRoute) {
        this.$router.push(this.backRoute);
      } else {
        this.$router.go(-1);
      }
    },
    searchItem() {
      this.$store.commit('setSearchedItemId', {
        'searched_tagid': this.search,
        'searched_ird': this.toggle_button
      });
      if (this.search) {
        this.drawerMenu = false;
        setTimeout(() => {
          this.$router.push({
            name: 'ItemInfo',
            query: { search: this.search, key: Date.now() }
          });
        }, 200);
      }
    },
    toggleButtonValue() {
      this.search = '';
      this.$store.commit('setSearchedItemId', {
        'searched_tagid': this.search,
        'searched_ird': this.toggle_button
      });
    },
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    async signOut() {
      if (this.logoutInProgress) return;
      this.logoutInProgress = true;
      this.drawerMenu = false;
      try {
        await this.$httpSecured.delete("/api/v1/logout");
        this.$store.commit("unsetCurrentUser");
        this.$store.commit("unsetSelectedLot");
        this.$store.commit("setCurrentWarehouse", { distribution_id: "" });
        this.$toast.success("Logout successful");
        this.$store.commit("setFTALogistics", { fta_logistics: null });
        this.$router.replace("/");
      } catch (error) {
        this.$store.commit("unsetCurrentUser");
        this.$store.commit("unsetSelectedLot");
        this.$router.replace("/");
      } finally {
        this.logoutInProgress = false;
      }
    },
    navigateHome(){
      this.$router.push({ name: 'reverseDisposition' });
    }
  }
});
</script>

<style scoped>
.mobile-header-container {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: linear-gradient(90deg, #003C9E 0%, #0052cc 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  width: 100%;
}

.mobile-header {
  min-height: 56px;
  padding: 0 12px;
}

.mobile-header-left {
  display: flex;
  align-items: center;
}

.mobile-header-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
}

.mobile-logo-img {
  width: 26px;
  height: 26px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.mobile-title {
  flex: 1;
  min-width: 0;
}

.mobile-title-text {
  color: white;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.mobile-header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.mobile-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-menu-btn {
  width: 40px !important;
  height: 40px !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Navigation Menu */
.mobile-nav-menu {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 220px;
}

.mobile-user-info {
  padding: 12px 16px !important;
  background: #f5f7ff;
}

.mobile-profile-name {
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #1a237e !important;
}

.mobile-profile-email {
  font-size: 12px !important;
  color: #666 !important;
}

.mobile-nav-item {
  min-height: 44px !important;
}

.mobile-nav-item-text {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #333 !important;
}

.mobile-logout-text {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #f44336 !important;
}

/* Touch-friendly */
.mobile-header * {
  -webkit-tap-highlight-color: transparent;
}

.mobile-menu-btn:active {
  transform: scale(0.95);
  transition: transform 0.1s ease;
}

@media (max-width: 480px) {
  .mobile-header {
    padding: 0 8px;
  }

  .mobile-title-text {
    font-size: 18px;
  }

  .mobile-logo {
    width: 32px;
    height: 32px;
  }

  .mobile-logo-img {
    width: 22px;
    height: 22px;
  }
}
.bottom-sheet-custom {
  margin: 0 !important;
  align-self: flex-end !important;
}
.location-autocomplete {
  font-size: 14px;
}
</style>
